import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Repeat, Check } from "lucide-react";
import { detectRecurring, fmtMoney, uid } from "@/lib/finance";
import { EmptyState } from "@/components/shared";

export function RecurringBillsDialog({ open, onOpenChange, transactions, bills, onAdd }) {
  const candidates = useMemo(
    () => (open ? detectRecurring(transactions, bills) : []),
    [open, transactions, bills]
  );
  const [selected, setSelected] = useState({});

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));

  const addSelected = () => {
    const chosen = candidates.filter((c) => selected[c.key]);
    const newBills = chosen.map((c) => ({
      id: uid(),
      name: c.name,
      amount: c.amount,
      category: c.category,
      dueDay: c.dueDay,
      isSubscription: c.amount < 60,
      paid: false,
    }));
    onAdd(newBills);
    setSelected({});
    onOpenChange(false);
  };

  const chosenCount = candidates.filter((c) => selected[c.key]).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--surface-2)] border border-[color:var(--border-subtle)] max-w-lg" data-testid="recurring-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl text-[var(--text)]">Recurring charges found</DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            We spotted repeating charges in your transactions. Select the ones to track as bills.
          </DialogDescription>
        </DialogHeader>

        {candidates.length === 0 ? (
          <EmptyState icon={Repeat} title="No recurring charges detected" message="We look for merchants that appear at least twice with similar amounts." />
        ) : (
          <div className="space-y-2 max-h-[360px] overflow-auto py-1">
            {candidates.map((c) => (
              <label key={c.key} className="flex items-center gap-3 rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5 cursor-pointer hover:border-[color:rgba(31,115,85,0.4)] transition-colors" data-testid={`recurring-candidate-${c.key.replace(/\s+/g, '-')}`}>
                <Checkbox checked={!!selected[c.key]} onCheckedChange={() => toggle(c.key)} data-testid={`recurring-check-${c.key.replace(/\s+/g, '-')}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--text)] capitalize truncate">{c.name}</div>
                  <div className="text-[11px] text-[var(--text-faint)]">Seen {c.count}× · {c.category} · around day {c.dueDay}</div>
                </div>
                <span className="tabular-nums text-sm text-[var(--text)]">{fmtMoney(c.amount)}</span>
              </label>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[color:var(--border-subtle)]">Not now</Button>
          {candidates.length > 0 && (
            <Button onClick={addSelected} disabled={chosenCount === 0} className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="add-recurring-button">
              <Check className="h-4 w-4 mr-1" />Track {chosenCount || ""} as bill{chosenCount === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
